/* Delivery date rules: one delivery day, Monday-Friday only, Dutch public holidays excluded.
   Orders placed before the cutoff hour (Amsterdam time) on a working day count from that day,
   otherwise from the next working day. Earliest delivery = order day + lead working days
   (default 3: Monday -> Thursday, Friday -> Wednesday). Orders from before the switch keep
   their original 3-day delivery period in all displays.
   Updated: Added describeOrderDelivery for the delivery/pickup line on invoices. */

import { addDays, format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from './supabase';

export interface DeliveryLeadTime {
  lead_working_days: number;
  cutoff_hour: number;
}

export const DEFAULT_LEAD_TIME: DeliveryLeadTime = { lead_working_days: 3, cutoff_hour: 18 };

const SINGLE_DAY_DELIVERY_SINCE = new Date('2026-09-30T22:00:00Z');

const toKey = (d: Date) => format(d, 'yyyy-MM-dd');

function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

const holidayCache = new Map<number, Set<string>>();

export function getDutchHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const easter = easterSunday(year);
  const kingsDay = new Date(year, 3, 27);
  if (kingsDay.getDay() === 0) kingsDay.setDate(26);
  const days = [
    new Date(year, 0, 1),
    addDays(easter, 1),
    kingsDay,
    addDays(easter, 39),
    addDays(easter, 50),
    new Date(year, 11, 25),
    new Date(year, 11, 26),
  ];
  // Bevrijdingsdag is only a general day off in lustrum years
  if (year % 5 === 0) days.push(new Date(year, 4, 5));
  const set = new Set(days.map(toKey));
  holidayCache.set(year, set);
  return set;
}

export function isWorkingDay(date: Date): boolean {
  const dow = date.getDay();
  if (dow === 0 || dow === 6) return false;
  return !getDutchHolidays(date.getFullYear()).has(toKey(date));
}

function amsterdamNow(now: Date): { date: Date; hour: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find(p => p.type === t)?.value);
  return { date: new Date(get('year'), get('month') - 1, get('day')), hour: get('hour') };
}

export function getEarliestDeliveryDate(settings: DeliveryLeadTime, now: Date = new Date()): Date {
  const { date, hour } = amsterdamNow(now);
  let orderDay = date;
  if (!isWorkingDay(orderDay) || hour >= settings.cutoff_hour) {
    do { orderDay = addDays(orderDay, 1); } while (!isWorkingDay(orderDay));
  }
  let result = orderDay;
  let added = 0;
  while (added < settings.lead_working_days) {
    result = addDays(result, 1);
    if (isWorkingDay(result)) added++;
  }
  return result;
}

export function isSelectableDeliveryDate(date: Date, earliest: Date): boolean {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return isWorkingDay(d) && d.getTime() >= earliest.getTime();
}

export async function fetchDeliveryLeadTime(): Promise<DeliveryLeadTime> {
  const { data, error } = await supabase
    .from('delivery_settings')
    .select('setting_value')
    .eq('setting_key', 'delivery_lead_time')
    .maybeSingle();
  if (error || !data?.setting_value) return DEFAULT_LEAD_TIME;
  const v = data.setting_value as Partial<DeliveryLeadTime>;
  return {
    lead_working_days: Number.isFinite(v.lead_working_days) ? Number(v.lead_working_days) : DEFAULT_LEAD_TIME.lead_working_days,
    cutoff_hour: Number.isFinite(v.cutoff_hour) ? Number(v.cutoff_hour) : DEFAULT_LEAD_TIME.cutoff_hour,
  };
}

function parseLocalDate(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isLegacyDeliveryPeriod(createdAt?: string | null): boolean {
  return !!createdAt && new Date(createdAt) < SINGLE_DAY_DELIVERY_SINCE;
}

export function formatDeliveryDay(deliveryDate: string, createdAt?: string | null): string {
  const start = parseLocalDate(deliveryDate);
  if (Number.isNaN(start.getTime())) return deliveryDate;
  if (isLegacyDeliveryPeriod(createdAt)) {
    return `${format(start, 'd MMM', { locale: nl })} - ${format(addDays(start, 2), 'd MMM yyyy', { locale: nl })}`;
  }
  return format(start, 'EEEE d MMMM yyyy', { locale: nl });
}

export interface OrderDeliveryInfo {
  label: string;
  value: string;
  note?: string;
}

export function describeOrderDelivery(order: {
  delivery_type?: string | null;
  delivery_week?: string | null;
  pickup_timeslot?: string | null;
  created_at?: string | null;
}): OrderDeliveryInfo | undefined {
  if (order.delivery_type === 'delivery' && order.delivery_week) {
    const value = formatDeliveryDay(order.delivery_week, order.created_at);
    if (isLegacyDeliveryPeriod(order.created_at)) {
      return {
        label: 'Leverperiode',
        value,
        note: 'We nemen dichterbij de leverdatum contact op voor de precieze leverdatum.',
      };
    }
    return { label: 'Bezorgdag', value: value.charAt(0).toUpperCase() + value.slice(1) };
  }
  if (order.delivery_type === 'pickup' && order.pickup_timeslot) {
    return { label: 'Afhalen', value: order.pickup_timeslot };
  }
  return undefined;
}
